import { MainLayoutContainer } from "components/Layout";
import { Add, FileUpload } from "@mui/icons-material";
import { REQUIREMENT, TABLE_ACTIONS } from "helpers/constants";
import { Link, useNavigate } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { useRequirement } from "pages/Requirements/list/useRequirement";
import CustomTable from "components/Table/CustomTable";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import { ResponsiveButton, ResponsiveButtonLink } from "components/Button";
import { Link as LinkBase, Stack } from "@mui/material";
import { AddRequirementSelector } from "./components";
import { useAuthUser } from "hooks/useAuthUser";
import { ROLES } from "types/user";
import { DomainColorSquares } from "components/Requirements";
import { useMemo } from "react";
import { requirementsHaveDomainSwatches } from "utils/requirementDomains";

export const ITERList = () => {
  const navigate = useNavigate();
  const { role } = useAuthUser();
  const isAdmin = role === ROLES.ADMIN;
  const {
    allRequirements,
    loading,
    isOrgView,
    deleteRequirementOpen,
    organizationId,
    requirements,
    handleAddSelectedRequirement,
    handleActionClick,
    setDeleteRequirementOpen,
    handleDelete,
  } = useRequirement(REQUIREMENT.REQUIREMENT_ITER);

  const showDomainsColumn = useMemo(
    () => requirementsHaveDomainSwatches(requirements),
    [requirements],
  );

  return (
    <MainLayoutContainer
      title="Curriculum Requirements - ITERs"
      loading={loading}
      error={undefined}
      buttonActions={
        <Stack direction="row" spacing={2}>
          {isOrgView && (
            <AddRequirementSelector
              requirements={allRequirements}
              onSelect={(requirement) => {
                handleAddSelectedRequirement(requirement);
              }}
              selectedRequirement={requirements}
            />
          )}
          {isAdmin && (
            <ResponsiveButtonLink
              to={
                isOrgView && organizationId
                  ? AdminRoutes.OrganizationUploadSpreadsheetFramework.replace(
                      ":organizationId",
                      organizationId,
                    )
                  : AdminRoutes.UploadSpreadsheetFramework
              }
              icon={<FileUpload />}
            >
              Import spreadsheet
            </ResponsiveButtonLink>
          )}
          <ResponsiveButton
            onClick={() => {
              navigate(
                organizationId
                  ? `/organization/${organizationId}/iter/create`
                  : "/iter/create",
              );
            }}
            icon={<Add />}
          >
            Create New ITER
          </ResponsiveButton>
        </Stack>
      }
    >
      <CustomTable
        entities={requirements}
        handleActionClick={handleActionClick}
        fieldToFilter={(row: any, filterText: string) => {
          const competenciesText =
            row.competencies
              ?.map((c: any) => c.competency?.name)
              .filter(Boolean)
              .join(" ")
              .toLowerCase() || "";
          return (
            row.name.toLowerCase().includes(filterText.toLowerCase()) ||
            row.description?.toLowerCase().includes(filterText.toLowerCase()) ||
            competenciesText.includes(filterText.toLowerCase())
          );
        }}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "description",
            label: "Description",
            value: (row: any) => {
              return row.description?.length > 128
                ? row.description.slice(0, 128)
                : row.description;
            },
          },
          {
            id: "competencies",
            label: "Competencies",
            value: (row: any) => {
              const comps =
                row.competencies
                  ?.map((c: any) => c.competency?.name)
                  .filter(Boolean) || [];
              return comps.length > 0 ? comps.join(", ") : "No competencies";
            },
          },
          ...(showDomainsColumn
            ? [
                {
                  id: "domains",
                  label: "Domains",
                  value: (row: any) => (
                    <DomainColorSquares requirement={row} />
                  ),
                },
              ]
            : []),
          {
            id: "requirement_id",
            value: (row: any) => (
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <LinkBase
                  sx={{ cursor: "pointer" }}
                  component={Link}
                  to={
                    organizationId
                      ? `/organization/${organizationId}/iter/${row.requirement_id}`
                      : `/iter/${row.requirement_id}`
                  }
                >
                  View
                </LinkBase>
              </Stack>
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[
          TABLE_ACTIONS.VIEW,
          TABLE_ACTIONS.EDIT,
          TABLE_ACTIONS.DELETE,
        ]}
        extraActionItems={[]}
        idColumn={"requirement_id"}
        enableCardViewInSmallDevices={true}
      />
      {deleteRequirementOpen && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={`Are you sure you want to ${
            isOrgView ? "remove" : "delete"
          } this Requirement?`}
          show={deleteRequirementOpen}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteRequirementOpen(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
};

